// Approximate effective property-tax rate (annual tax / market value) and
// annual insurance rate (annual premium / market value) by US state.
// Sources: Tax Foundation 2024, Insurify 2024. Use as "typical" defaults —
// investors should still verify at the county / carrier level.
export type StateRateInfo = {
  code: string; // 2-letter USPS
  name: string;
  effectivePropertyTaxRate: number; // e.g. 0.0083 for 0.83%
  insuranceRate: number; // e.g. 0.0055 for 0.55%
};

export const STATE_RATES: StateRateInfo[] = [
  { code: "AL", name: "Alabama", effectivePropertyTaxRate: 0.0041, insuranceRate: 0.0075 },
  { code: "AK", name: "Alaska", effectivePropertyTaxRate: 0.0119, insuranceRate: 0.0035 },
  { code: "AZ", name: "Arizona", effectivePropertyTaxRate: 0.0063, insuranceRate: 0.0045 },
  { code: "AR", name: "Arkansas", effectivePropertyTaxRate: 0.0064, insuranceRate: 0.0065 },
  { code: "CA", name: "California", effectivePropertyTaxRate: 0.0075, insuranceRate: 0.0045 },
  { code: "CO", name: "Colorado", effectivePropertyTaxRate: 0.0055, insuranceRate: 0.0075 },
  { code: "CT", name: "Connecticut", effectivePropertyTaxRate: 0.0214, insuranceRate: 0.0055 },
  { code: "DE", name: "Delaware", effectivePropertyTaxRate: 0.0061, insuranceRate: 0.0035 },
  { code: "DC", name: "District of Columbia", effectivePropertyTaxRate: 0.0062, insuranceRate: 0.0035 },
  { code: "FL", name: "Florida", effectivePropertyTaxRate: 0.0091, insuranceRate: 0.0125 },
  { code: "GA", name: "Georgia", effectivePropertyTaxRate: 0.0092, insuranceRate: 0.0055 },
  { code: "HI", name: "Hawaii", effectivePropertyTaxRate: 0.0032, insuranceRate: 0.0035 },
  { code: "ID", name: "Idaho", effectivePropertyTaxRate: 0.0069, insuranceRate: 0.0035 },
  { code: "IL", name: "Illinois", effectivePropertyTaxRate: 0.0223, insuranceRate: 0.0055 },
  { code: "IN", name: "Indiana", effectivePropertyTaxRate: 0.0085, insuranceRate: 0.0045 },
  { code: "IA", name: "Iowa", effectivePropertyTaxRate: 0.0157, insuranceRate: 0.0055 },
  { code: "KS", name: "Kansas", effectivePropertyTaxRate: 0.0141, insuranceRate: 0.0085 },
  { code: "KY", name: "Kentucky", effectivePropertyTaxRate: 0.0086, insuranceRate: 0.0055 },
  { code: "LA", name: "Louisiana", effectivePropertyTaxRate: 0.0055, insuranceRate: 0.0115 },
  { code: "ME", name: "Maine", effectivePropertyTaxRate: 0.0128, insuranceRate: 0.0035 },
  { code: "MD", name: "Maryland", effectivePropertyTaxRate: 0.0106, insuranceRate: 0.0035 },
  { code: "MA", name: "Massachusetts", effectivePropertyTaxRate: 0.0117, insuranceRate: 0.0045 },
  { code: "MI", name: "Michigan", effectivePropertyTaxRate: 0.0138, insuranceRate: 0.0055 },
  { code: "MN", name: "Minnesota", effectivePropertyTaxRate: 0.0111, insuranceRate: 0.0075 },
  { code: "MS", name: "Mississippi", effectivePropertyTaxRate: 0.0081, insuranceRate: 0.0095 },
  { code: "MO", name: "Missouri", effectivePropertyTaxRate: 0.0097, insuranceRate: 0.0075 },
  { code: "MT", name: "Montana", effectivePropertyTaxRate: 0.0074, insuranceRate: 0.0055 },
  { code: "NE", name: "Nebraska", effectivePropertyTaxRate: 0.0163, insuranceRate: 0.0085 },
  { code: "NV", name: "Nevada", effectivePropertyTaxRate: 0.0059, insuranceRate: 0.0035 },
  { code: "NH", name: "New Hampshire", effectivePropertyTaxRate: 0.0189, insuranceRate: 0.0035 },
  { code: "NJ", name: "New Jersey", effectivePropertyTaxRate: 0.0247, insuranceRate: 0.0045 },
  { code: "NM", name: "New Mexico", effectivePropertyTaxRate: 0.008, insuranceRate: 0.0055 },
  { code: "NY", name: "New York", effectivePropertyTaxRate: 0.0173, insuranceRate: 0.0045 },
  { code: "NC", name: "North Carolina", effectivePropertyTaxRate: 0.0082, insuranceRate: 0.0055 },
  { code: "ND", name: "North Dakota", effectivePropertyTaxRate: 0.0098, insuranceRate: 0.0075 },
  { code: "OH", name: "Ohio", effectivePropertyTaxRate: 0.0159, insuranceRate: 0.0045 },
  { code: "OK", name: "Oklahoma", effectivePropertyTaxRate: 0.009, insuranceRate: 0.0125 },
  { code: "OR", name: "Oregon", effectivePropertyTaxRate: 0.0097, insuranceRate: 0.0035 },
  { code: "PA", name: "Pennsylvania", effectivePropertyTaxRate: 0.0149, insuranceRate: 0.0045 },
  { code: "RI", name: "Rhode Island", effectivePropertyTaxRate: 0.0163, insuranceRate: 0.0045 },
  { code: "SC", name: "South Carolina", effectivePropertyTaxRate: 0.0057, insuranceRate: 0.0075 },
  { code: "SD", name: "South Dakota", effectivePropertyTaxRate: 0.0132, insuranceRate: 0.0075 },
  { code: "TN", name: "Tennessee", effectivePropertyTaxRate: 0.0071, insuranceRate: 0.0075 },
  { code: "TX", name: "Texas", effectivePropertyTaxRate: 0.018, insuranceRate: 0.0115 },
  { code: "UT", name: "Utah", effectivePropertyTaxRate: 0.0063, insuranceRate: 0.0045 },
  { code: "VT", name: "Vermont", effectivePropertyTaxRate: 0.019, insuranceRate: 0.0035 },
  { code: "VA", name: "Virginia", effectivePropertyTaxRate: 0.0082, insuranceRate: 0.0045 },
  { code: "WA", name: "Washington", effectivePropertyTaxRate: 0.0098, insuranceRate: 0.0045 },
  { code: "WV", name: "West Virginia", effectivePropertyTaxRate: 0.0059, insuranceRate: 0.0055 },
  { code: "WI", name: "Wisconsin", effectivePropertyTaxRate: 0.0176, insuranceRate: 0.0045 },
  { code: "WY", name: "Wyoming", effectivePropertyTaxRate: 0.0061, insuranceRate: 0.0055 },
];

export function getStateRate(code: string): StateRateInfo | undefined {
  return STATE_RATES.find((s) => s.code === code.toUpperCase());
}
